import type * as React from "react";

import { cn } from "../lib/cn";

type PaperPanelVariant = "card" | "recessed" | "page";

/** Surface + border only. Shadow is decided separately (see `lifted`). */
const variantSurface: Record<PaperPanelVariant, string> = {
  // a sheet laid on top of the page
  card: "bg-card border border-rule",
  // a well cut into the page: darker paper, no border, no shadow
  recessed: "bg-recess",
  // a fresh page in its own right
  page: "bg-paper border border-rule",
};

export interface PaperPanelProps extends React.HTMLAttributes<HTMLDivElement> {
  /** card = paper on the page (default), recessed = a well cut into it, page = a page of its own. */
  variant?: PaperPanelVariant;
  /** Hold the sheet further off the page. Ignored by `recessed`, which is sunk in. */
  lifted?: boolean;
  ref?: React.Ref<HTMLDivElement>;
}

/**
 * A sheet of paper. The workhorse surface behind most of the product: panels,
 * sections, cards, wells. Everything that is not a named object (folder, index
 * card, sticky) is a PaperPanel.
 *
 * No padding is applied - a sheet has no opinion about its contents.
 */
export function PaperPanel({
  variant = "card",
  lifted = false,
  className,
  ...rest
}: PaperPanelProps) {
  // A recess is a hole in the page; a hole cannot cast a shadow upward, so
  // `lifted` has nothing to act on there.
  const shadow =
    variant === "recessed"
      ? undefined
      : lifted
        ? "shadow-lift-lg"
        : variant === "card"
          ? "shadow-lift"
          : undefined;

  return (
    <div
      className={cn(
        variantSurface[variant],
        "rounded-paper",
        shadow,
        className,
      )}
      {...rest}
    />
  );
}
